<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <ProgressBar active-step="Tell us more about" />
    <PageHeading
      :default-heading="pageHeader"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          {{ pd_content.personal_information_section_header }}
        </h2>
        <apx-select
          id="residenceCountryList"
          :value="su_residenceCountry"
          :label="pd_content.citizenship_country_label"
          :error-text="pd_content.citizenship_country_error"
          required
        />
        <apx-phone
          v-if="showPhone"
          id="phoneNumber"
          :telephone-number="su_dayPhone"
          :telephone-number-label="pd_content.phone_number_label.value"
          required
        />
        <apx-email
          id="email"
          :value="su_email"
          :label="pd_content.email_label"
          required
        />
      </div>
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          {{ replaceName(pd_content.addressSection.header) }}
        </h2>
        <p class="m-small-top">
          {{ replaceName(pd_content.addressSection.paragraph) }}
        </p>
        <div class="pvd-form-row">
          <div class="form-row-label form-row-label-top labelIconHack">
            <label>{{ pd_content.addressSection.address_label }}</label>
          </div>
          <div class="form-row-content">
            <div class="pvd-radio-btn">
              <input
                id="resAddressOption1"
                type="radio"
                name="resAddressOption"
                @click="primaryAddressSelected"
              >
              <label
                for="resAddressOption1"
                v-html="primaryAddress"
              />
            </div>
            <div class="pvd-radio-btn">
              <input
                id="resAddressOption2"
                type="radio"
                name="resAddressOption"
                @click="showNewAddressSection"
              >
              <label for="resAddressOption2">{{ pd_content.addressSection.address_option2 }}</label>
            </div>
          </div>
        </div>
        <apx-address
          v-if="showNewAddress"
          id="address"
          :address-line1="su_leg_line1"
          :address-line2="su_leg_line2"
          :city-name="su_leg_city"
          :state-code="su_leg_state"
          :zip-code="su_leg_zip"
          :address-labels="JSON.stringify(addressLabels)"
          prevent-pobox
          required
        />
      </div>
      <div class="pvd-container">
        <PageNavigation
          show-primary-button-spinner="N"
          enable-exit-modal="Y"
          show-secondary-button="Y"
          show-page-spinner="N"
          primary-button-type="button"
          :primary-button-label="getPrimaryButtonLabel"
          :secondary-button-label="getSecondaryButtonLabel"
          :page-exit-url="computedExitUrl"
          @primary-button-clicked="nextButtonClicked"
          @secondary-button-clicked="seondaryButtonClicked"
        />
      </div>
    </form>
  </div>
</template>

<script>
import ProgressBar from '../components/ProgressBar.vue';
import PageHeading from '../components/PageHeading.vue';
import PageNavigation from '../components/PageNavigation.vue';
import dataHelper from '../mixins/dataHelper';
import utility from '../mixins/utility';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import youthHelper from '../mixins/youthHelper';
import validationHelper from '../mixins/validationHelper';
import addressAutocomplete from '../mixins/addressAutocomplete';
import dataSanitize from '../mixins/dataSanitize';

export default {
  name: 'JointContactInformation',
  components: {
    ProgressBar,
    PageHeading,
    PageNavigation,
  },
  mixins: [dataHelper, pageNavigationHelper, utility, youthHelper, validationHelper, addressAutocomplete, dataSanitize],
  data() {
    return {
      showNewAddress: false,
      changeEventName: 'value-changed',
    };
  },
  computed: {
    isManagedAccount() {
      return this.isManaged(this.routeId);
    },
    pageHeader() {
      const pageHeader = this.pd_content.page_header;
      if (pageHeader) {
        const heading = this.cs_editMode ? this.replaceName(this.pd_content.page_header.edit)
          : this.replaceName(this.pd_content.page_header.default);

        if (heading) {
          document.title = this.setTitle(heading);
        }
        return heading;
      }
      return '';
    },
    addressLabels() {
      return {
        addressLine1: this.pd_content.addressSection.address_line1_label,
        addressLine2: this.pd_content.addressSection.address_line2_label,
        cityName: this.pd_content.addressSection.city_label,
        stateCode: this.pd_content.addressSection.state_label,
        zipCode: this.pd_content.addressSection.zipcode_label,
      };
    },
    primaryAddress() {
      return `${this.pu_leg_line1}${this.pu_leg_line2 ? `<br>${this.pu_leg_line2}` : ''}
        <br>${this.pu_leg_city}, ${this.pu_leg_state} ${this.pu_leg_zip}`;
    },
    showPhone() {
      return !this.su_isCustomer || this.routeId === 'youth';
    },
  },
  created() {
    if (this.cs_editMode) {
      this.unsubscribe = this.sanitizeInstanceData('secondaryUser');
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  mounted() {
    this.trackEdit(this.cs_editMode, 'edit-joint-contact-information');
    this.populateCountryList('#residenceCountryList');

    document.querySelector('#residenceCountryList')
      .addEventListener(this.changeEventName, this.residenceCountryChanged);

    if (this.showPhone) {
      const phoneComponent = document.querySelector('#phoneNumber');
      phoneComponent.addEventListener(this.changeEventName, this.phoneNumberChanged);
      phoneComponent.validationRules = this.buildRules('phone');
    }

    const emailComponent = document.querySelector('#email');
    emailComponent.addEventListener(this.changeEventName, this.emailChanged);
    emailComponent.validationRules = this.buildRules('email');

    this.initialNewAddressProvided = this.$globalData.jointContactInformation.newAddressProvided;

    if (this.$globalData.jointContactInformation.newAddressProvided) {
      this.showNewAddress = true;
      document.querySelector('#resAddressOption2').checked = true;
      this.setAddressListener();
    } else {
      document.querySelector('#resAddressOption1').checked = true;
      this.setPrimaryAddressToSecondaryUser();
    }
  },
  methods: {
    nextButtonClicked(navAction) {
      this.performPageValidation(navAction).then((valid) => {
        if (valid) {
          this.performNavigation(this.pd_content.primary_button.value);
        }
      });
    },
    seondaryButtonClicked() {
      if (this.cs_editMode) {
        this.$globalData.jointContactInformation.newAddressProvided = this.initialNewAddressProvided;
      }
    },
    residenceCountryChanged(event) {
      this.commitSecondaryUserData(event.detail.selectValue, 'residenceCountry');
    },
    phoneNumberChanged(event) {
      this.commitSecondaryUserData(event.detail.telephoneNumber, 'dayPhone');
      this.$globalData.jointContactInformation.dayPhoneEdited = true;
      this.commitSecondaryUserData(event.detail.checked, 'mobilePhoneCheckIndicator');
    },
    emailChanged(event) {
      this.commitSecondaryUserData(event.detail.value, 'email');
      this.$globalData.jointContactInformation.emailEdited = true;
    },
    addressChanged(event) {
      this.commitSecondaryUserData(event.detail.address.addressLine1, 'addresses.legal.line1');
      this.commitSecondaryUserData(event.detail.address.addressLine2, 'addresses.legal.line2');
      this.commitSecondaryUserData(event.detail.address.cityName, 'addresses.legal.city');
      this.commitSecondaryUserData(event.detail.address.stateCode, 'addresses.legal.state');
      this.commitSecondaryUserData(event.detail.address.zipCode, 'addresses.legal.zip');
      this.$globalData.jointContactInformation.newAddressProvided = true;
    },
    checkEmailPrefillError() {
      return this.safeObjectGet(this.su_formErrors, 'jointContactInformation.email.error');
    },
    checkPhonePrefillError() {
      return this.safeObjectGet(this.su_formErrors, 'jointContactInformation.telephoneNumber.error');
    },
    fillAddressValues(address) {
      const event = {
        detail: {
          address,
        },
      };

      const line1Element = document.getElementById('address-addressLine1');
      line1Element.value = event.detail.address.addressLine1;
      line1Element.dispatchEvent(new Event('change'));

      document.getElementById('address-addressLine2').value = event.detail.address.addressLine2;

      const cityNameElement = document.getElementById('address-cityName');
      cityNameElement.value = event.detail.address.cityName;
      cityNameElement.dispatchEvent(new Event('change'));

      const stateCodeElement = document.getElementById('address-stateCode');
      stateCodeElement.value = event.detail.address.stateCode;
      stateCodeElement.dispatchEvent(new Event('change'));

      const zipCodeElement = document.getElementById('address-zipCode');
      zipCodeElement.value = event.detail.address.zipCode;
      zipCodeElement.dispatchEvent(new Event('change'));

      this.addressChanged(event);
    },
    setAddressListener() {
      setTimeout(() => {
        const addressComponent = document.querySelector('#address');
        if (addressComponent) {
          addressComponent.addEventListener(this.changeEventName, this.addressChanged);
          addressComponent.validationRules = this.buildRules('address');
          this.initGoogleAutocomplete('address-addressLine1', this.fillAddressValues);
        }
      }, 400);
    },
    showNewAddressSection() {
      this.showNewAddress = true;
      this.setAddressListener();
      this.commitSecondaryUserData('', 'addresses.legal.line1');
      this.commitSecondaryUserData('', 'addresses.legal.line2');
      this.commitSecondaryUserData('', 'addresses.legal.city');
      this.commitSecondaryUserData('', 'addresses.legal.state');
      this.commitSecondaryUserData('', 'addresses.legal.zip');
    },
    primaryAddressSelected() {
      this.showNewAddress = false;
      this.$globalData.jointContactInformation.newAddressProvided = false;
      this.setPrimaryAddressToSecondaryUser();
    },
    setPrimaryAddressToSecondaryUser() {
      if (this.pu_leg_line1 && this.pu_leg_city && this.pu_leg_state && this.pu_leg_zip) {
        this.commitSecondaryUserData(this.pu_leg_line1, 'addresses.legal.line1');
        this.commitSecondaryUserData(this.pu_leg_line2, 'addresses.legal.line2');
        this.commitSecondaryUserData(this.pu_leg_city, 'addresses.legal.city');
        this.commitSecondaryUserData(this.pu_leg_state, 'addresses.legal.state');
        this.commitSecondaryUserData(this.pu_leg_zip, 'addresses.legal.zip');
      }
    },
  },
};
</script>
